import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CrearCotizacionPostventaVista } from '../vista/crearCotizacionPostventaVista';
import {
    cotizacionPostventaInicial,
    nuevoConcepto,
    nuevoRepuesto,
    resultadoSinCalcular,
    type CampoCotizacionPostventa,
    type SeccionConceptos
} from '../interfaces';
import { configuracionPostventaInicial } from '../../configuracionCotizadorPostventa/interfaces';
import { useEdicionFilas, type ICoordinacionEdicionProps, type ITablaEditable } from '../../../globales/componentes/tabla/useEdicionFilas';
import type { IConceptoCostoPostventa, ICotizacionPostventa, IRepuestoPostventa } from '../../../modelos/CotizadorPostventa/entidades/ICotizacionPostventa';
import { useAutenticacion } from '../../../globales/contextos/authContext';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { EnumTipoNotificacion } from '../../../globales/interfaces';

interface CrearCotizacionPostventaCVProps {
    /** Cotización a editar; sin ella se crea una nueva. */
    cotizacionInicial?: ICotizacionPostventa;
    alGuardar: (cotizacion: ICotizacionPostventa) => void;
    alVolver: () => void;
}

const vacio = (valor: unknown) => valor === null || valor === undefined || String(valor).trim() === '';

/** Firma de los datos que entran al cálculo: si cambia después de calcular, hay que recalcular. */
const firmaCalculo = (cotizacion: ICotizacionPostventa) => JSON.stringify({ ...cotizacion, Resultado: undefined });

/** Estado de la cotización: campos generales aquí, cada tabla en su propio useEdicionFilas. */
export const CrearCotizacionPostventaCV: React.FC<CrearCotizacionPostventaCVProps> = ({ cotizacionInicial, alGuardar, alVolver }) => {
    const { usuario } = useAutenticacion();
    const { mostrarNotificacion } = useNotificaciones();

    // TODO back: la configuración (valores por defecto e incoterms) se consulta del servicio.
    const configuracion = configuracionPostventaInicial;
    const incoterms = useMemo(() => configuracion.Incoterms.map(i => ({ value: i.Id, label: i.Codigo })), [configuracion]);

    const [inicial] = useState<ICotizacionPostventa>(() => cotizacionInicial ?? {
        ...cotizacionPostventaInicial(configuracion),
        FechaCreacion: new Date().toISOString(),
        UsuarioCreadorId: usuario?.Id,
        UsuarioCreadorNombre: usuario?.NombreCompleto
    });
    const [general, setGeneral] = useState<ICotizacionPostventa>(inicial);
    const [errores, setErrores] = useState<Record<string, string>>({});
    const [firmaCalculada, setFirmaCalculada] = useState<string | null>(() => inicial.Resultado ? firmaCalculo(inicial) : null);

    // Una sola fila en edición entre todas las tablas de la cotización.
    const canceladoresRef = useRef<Record<string, () => void>>({});
    const registrarCancelacion = useCallback((clave: string, cancelar: () => void) => {
        canceladoresRef.current[clave] = cancelar;
    }, []);
    const cancelarTodasLasEdiciones = useCallback(() => {
        Object.values(canceladoresRef.current).forEach(cancelar => cancelar());
    }, []);
    useEffect(() => () => cancelarTodasLasEdiciones(), [cancelarTodasLasEdiciones]);
    const coordinacion: ICoordinacionEdicionProps = { onRegistrarCancelacion: registrarCancelacion, onIniciarEdicionGeneral: cancelarTodasLasEdiciones };

    // TODO back: guardar o eliminar cada fila (alGuardarFila / alEliminarFila) cuando la cotización ya exista.
    const repuestos = useEdicionFilas<IRepuestoPostventa>({
        ...coordinacion,
        clave: 'repuestos',
        datosIniciales: inicial.Repuestos,
        camposObligatorios: ['Item', 'CostoUnitario', 'Cantidad', 'Trm', 'IncotermId'],
        crearFila: nuevoRepuesto,
        permiteEliminar: true
    });
    const tablaConceptos = (clave: SeccionConceptos) => ({
        ...coordinacion,
        clave,
        datosIniciales: inicial[clave],
        camposObligatorios: ['Item', 'Costo'] as (keyof IConceptoCostoPostventa)[],
        crearFila: nuevoConcepto,
        permiteEliminar: true
    });
    const conceptos: Record<SeccionConceptos, ITablaEditable<IConceptoCostoPostventa>> = {
        Transportes: useEdicionFilas(tablaConceptos('Transportes')),
        Viaticos: useEdicionFilas(tablaConceptos('Viaticos')),
        KnowHow: useEdicionFilas(tablaConceptos('KnowHow')),
        OtrosCostos: useEdicionFilas(tablaConceptos('OtrosCostos'))
    };

    const cotizacion: ICotizacionPostventa = {
        ...general,
        Repuestos: repuestos.datos,
        Transportes: conceptos.Transportes.datos,
        Viaticos: conceptos.Viaticos.datos,
        KnowHow: conceptos.KnowHow.datos,
        OtrosCostos: conceptos.OtrosCostos.datos
    };
    const requiereRecalculo = !!cotizacion.Resultado && firmaCalculada !== firmaCalculo(cotizacion);
    const hayFilaEnEdicion = [repuestos, ...Object.values(conceptos)].some(t => t.meta.filaEnEdicion !== null);

    const alCambiar = (campo: CampoCotizacionPostventa, valor: string | number) => {
        setGeneral(prev => ({ ...prev, [campo]: valor }));
        setErrores(prev => ({ ...prev, [campo]: '' }));
    };

    // --- Validaciones por paso ---

    const validarPaso = (paso: number) => {
        const nuevosErrores: Record<string, string> = {};
        const requerido = (campo: CampoCotizacionPostventa, mensaje: string) => { if (vacio(general[campo])) nuevosErrores[campo] = mensaje; };
        const positivo = (campo: CampoCotizacionPostventa, mensaje: string) => { if (!(Number(general[campo]) > 0)) nuevosErrores[campo] = mensaje; };

        if (paso === 0) {
            requerido('Proyecto', 'El proyecto es obligatorio');
            requerido('Cliente', 'El cliente es obligatorio');
            requerido('Descripcion', 'Describe el trabajo a realizar');
            positivo('NumeroUnidades', 'Debe haber al menos una unidad');
        }
        if (paso === 2) {
            requerido('HorasHombre', 'Obligatorio');
            positivo('TarifaHoraHombre', 'Debe ser mayor que cero');
            requerido('TiempoFabricacion', 'Obligatorio');
            requerido('TiempoInstalacion', 'Obligatorio');
        }
        if (paso === 3) positivo('FactorPrecio', 'Debe ser mayor que cero');

        setErrores(prev => ({ ...prev, ...nuevosErrores }));
        return Object.keys(nuevosErrores).length === 0;
    };

    // --- Acciones del último paso ---

    const alCalcular = () => {
        const pasosValidos = [0, 1, 2, 3].map(validarPaso).every(Boolean);
        if (!pasosValidos) {
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Datos incompletos', 'Revisa los campos obligatorios de los pasos anteriores.');
            return;
        }
        // TODO back: enviar la cotización a calcular y guardar el resultado que devuelva.
        const calculada = { ...cotizacion, Resultado: resultadoSinCalcular(cotizacion) };
        setGeneral(prev => ({ ...prev, Resultado: calculada.Resultado }));
        setFirmaCalculada(firmaCalculo(calculada));
        mostrarNotificacion(EnumTipoNotificacion.Information, 'Cálculo pendiente', 'Los valores los calculará el servicio del cotizador; por ahora se muestran en cero.', 4000);
    };

    const alGenerarPdf = () => {
        // TODO back: previsualizar el PDF (carátula de la hoja PRECIO).
        mostrarNotificacion(EnumTipoNotificacion.Information, 'Pendiente', 'El PDF se habilitará al conectar el servicio del cotizador.', 3000);
    };

    const guardar = () => {
        if (!validarPaso(0)) {
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Datos incompletos', 'Revisa los datos generales de la cotización.');
            return;
        }
        alGuardar(cotizacion);
    };

    return (
        <CrearCotizacionPostventaVista
            cotizacion={cotizacion}
            repuestos={repuestos}
            conceptos={conceptos}
            incoterms={incoterms}
            errores={errores}
            hayFilaEnEdicion={hayFilaEnEdicion}
            requiereRecalculo={requiereRecalculo}
            alCambiar={alCambiar}
            validarPaso={validarPaso}
            alCalcular={alCalcular}
            alGenerarPdf={alGenerarPdf}
            alGuardar={guardar}
            alVolver={alVolver}
        />
    );
};
